import { useQuery } from "@tanstack/react-query";
import React from "react";
import { match, P } from "ts-pattern";
import { Page } from "../../../activities/AppPage";
import { API } from "../../../API";
import {
  AgencyId,
  CaregiverComplianceItemMobile,
  CaregiverDocumentTypeId,
  CaregiverUploadDocumentMobile,
} from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { assertDefined } from "../../../utils/assertUndefinedable";
import { QueryStatus } from "../../../utils/reactQueryUtils";
import ErrorScaffold from "../../ErrorScaffold";
import AppLoadingPlaceholder from "../../Generic/AppLoadingPlaceholder";
import AppModal from "../../Generic/AppModal/AppModal";
import { AppOverlay } from "../../Generic/AppOverlay";
import useModal from "../../useModal";
import { withMedflytAgencyId } from "../../withMedflytAgencyId";
import ComplianceItemScreen from "./ComplianceItemScreen";
import ComplianceUploadDocumentModal from "./ComplianceUploadDocumentModal";
import useUploadDocument from "./hooks/useUploadDocument";
import MyMedflytComplianceScreen from "./MyMedflytComplianceScreen";
import UploadGroupDocumentModal from "./UploadGroupDocumentModal";

interface Props {
  api: API;
  documentTypeId: CaregiverDocumentTypeId;
  medflytAgencyId: AgencyId;
  onClickGoBack: () => void;
  onRequestNavigate: (page: Page) => void;
}

function ComplianceItemActivity(props: Props) {
  const query = useQuery({
    queryKey: ["compliance_items", props.medflytAgencyId],
    queryFn: () => props.api.getComplianceItems(props.medflytAgencyId),
  });

  const handleNavigateToComplianceDocumentForm = (documentTypeId: CaregiverDocumentTypeId) => {
    props.onRequestNavigate({
      type: "ComplianceDocumentForm",
      documentTypeId,
    });
  };

  const handleClickComplianceDocument = (document: CaregiverUploadDocumentMobile) => {
    props.onRequestNavigate({ type: "ComplianceDocument", document });
  };

  return match(query)
    .with(QueryStatus.Error, () => (
      <ErrorScaffold
        title={translate(STRINGS.ONBOARDING)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickRetry={query.refetch}
        onClickGoBack={props.onClickGoBack}
      />
    ))
    .with(P.union(QueryStatus.Loading, QueryStatus.Idle), () => (
      <MyMedflytComplianceScreen isLoading={true} onClickGoBack={props.onClickGoBack} />
    ))
    .with(QueryStatus.ResolvedAs("data"), (result) => (
      <ResolvedComplianceItem
        {...props}
        item={assertDefined(
          result.data.items.find((item) => item.documentType.id === props.documentTypeId)
        )}
        onRequestRefresh={query.refetch}
        onRequestNavigateToComplianceDocumentForm={handleNavigateToComplianceDocumentForm}
        onClickComplianceDocument={handleClickComplianceDocument}
      />
    ))
    .exhaustive();
}

function ResolvedComplianceItem(props: {
  api: API;
  item: CaregiverComplianceItemMobile;
  medflytAgencyId: AgencyId;
  onClickComplianceDocument: (document: CaregiverUploadDocumentMobile) => void;
  onClickGoBack: () => void;
  onRequestRefresh: () => void;
  onRequestNavigateToComplianceDocumentForm: (documentTypeId: CaregiverDocumentTypeId) => void;
}) {
  const modal = useModal();

  const {
    uploadDocumentSelectionModal,
    uploadGroupDocumentModal,
    isLoading,
    onClickUploadDocument,
    onOpenCamera,
    onOpenGallery,
  } = useUploadDocument({
    api: props.api,
    item: props.item,
    agencyId: props.medflytAgencyId,
    onError: modal.setModalAsError,
    onRequestRefresh: props.onRequestRefresh,
    onRequestNavigateToComplianceDocumentForm: props.onRequestNavigateToComplianceDocumentForm,
  });

  return (
    <>
      <ComplianceItemScreen
        item={props.item}
        onClickComplianceDocument={props.onClickComplianceDocument}
        onClickUploadDocument={onClickUploadDocument}
        onClickGoBack={props.onClickGoBack}
      />
      <AppOverlay>
        <UploadGroupDocumentModal
          api={props.api}
          agencyId={props.medflytAgencyId}
          isOpen={uploadGroupDocumentModal.isOpen}
          item={props.item}
          onRequestClose={uploadGroupDocumentModal.actions.dismiss}
          onError={modal.setModalAsError}
          onRequestRefresh={props.onRequestRefresh}
          onRequestNavigateToComplianceDocumentForm={
            props.onRequestNavigateToComplianceDocumentForm
          }
        />
        <ComplianceUploadDocumentModal
          isOpen={uploadDocumentSelectionModal.isOpen}
          onClickOpenCamera={onOpenCamera}
          onClickOpenGallery={onOpenGallery}
          onRequestClose={uploadDocumentSelectionModal.actions.dismiss}
        />
        <AppModal {...modal.modalProps} />
        <AppLoadingPlaceholder
          primaryHeader={translate(STRINGS.UPLOADING)}
          secondaryHeader={translate(STRINGS.PLEASE_WAIT) + "..."}
          isOpen={isLoading}
        />
      </AppOverlay>
    </>
  );
}

export default withMedflytAgencyId(ComplianceItemActivity);
